import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/notifications/notification.service';
import { SessionService } from 'src/app/session.service';
import { UiService } from 'src/app/ui/ui.service';
import { pageSizeOptions } from 'src/app/shared/utils/pagination';
import { IntegrationsService } from '../../integrations/integrations.service';
import { LocalStorageService } from 'src/app/storage/local-storage.service';
import { UtilService } from 'src/app/util/util.service';
import { FileSaverService } from 'ngx-filesaver';
import moment from 'moment-timezone';

@Component({
  selector: 'app-feedback-list',
  templateUrl: './feedback-list.component.html',
  styleUrls: ['./feedback-list.component.scss']
})
export class FeedbackListComponent implements OnInit {
  pageSizeOptions = pageSizeOptions;
  filterForm!: FormGroup;
  displayedColumns = [
    'created_at',
    'user_name',
    'email',
    'rating',
    'shares',
    'device_info'
  ];
  feedbackData!: any[];
  feedbackDataSource!: MatTableDataSource<any>;
  @ViewChild('feedbackTable') feedbackTable!: MatTable<any[]>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  
  private onComponentDestroy = new Subject();
  perPage: number = 20;
  integrationId!: string;
  integration: any = null;
  includeTests: boolean = false;
  currentPage: number = 0;
  totalItems: number = 0;
  userTimezone: string = 'UTC';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private integrationsService: IntegrationsService,
    private sessionService: SessionService,
    private notificationService: NotificationService,
    private uiService: UiService,
    private utilSvc: UtilService,
    private localStorageSvc: LocalStorageService,
    private fileSaver: FileSaverService,
  ) {}

  ngOnInit(): void {
    this.perPage = this.localStorageSvc.pageSize || 20;
    this.integrationId = this.route.snapshot.params['integrationId'];
    this.integration = this.route.snapshot.data.integration;
    this.includeTests = this.parseIncludeTestsQuery(this.route.snapshot.queryParams['include_tests']);
    this.userTimezone = this.sessionService.currentUserValue?.timezone || 'UTC';

    // Use integration ID from resolved data if route param is not available
    if (!this.integrationId && this.integration?.id) {
      this.integrationId = this.integration.id.toString();
    }

    // Get dates from URL parameters or use defaults (last 7 days)
    let startDate: Date, endDate: Date;
    
    const today = moment.tz(this.userTimezone).endOf('day').toDate();
    
    if (this.route.snapshot.queryParams['start_date'] && this.route.snapshot.queryParams['end_date']) {
      const urlStartDate = this.parseDateFromQuery(this.route.snapshot.queryParams['start_date']);
      const urlEndDate = this.parseDateFromQuery(this.route.snapshot.queryParams['end_date']);
      
      // Validate dates are not in the future
      if (urlStartDate && urlEndDate && urlStartDate <= today && urlEndDate <= today) {
        startDate = urlStartDate;
        endDate = urlEndDate;
      } else {
        // Invalid dates in URL, use defaults and clear URL params  
        endDate = moment.tz(this.userTimezone).toDate(); // Today's date
        startDate = moment.tz(this.userTimezone).subtract(6, 'days').startOf('day').toDate();
        
        // Clear invalid URL parameters
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            start_date: this.formatDateForQuery(startDate),
            end_date: this.formatDateForQuery(endDate)
          },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    } else {
      // Check if we're coming from usage report with dates
      const usageStartDate = this.route.snapshot.queryParams['usage_start_date'];
      const usageEndDate = this.route.snapshot.queryParams['usage_end_date'];
      
      if (usageStartDate && usageEndDate) {
        const usageStart = this.parseDateFromQuery(usageStartDate);
        const usageEnd = this.parseDateFromQuery(usageEndDate);
        
        // Validate dates are not in the future
        if (usageStart && usageEnd && usageStart <= today && usageEnd <= today) {
          startDate = usageStart;
          endDate = usageEnd;
          
          // Convert usage dates to feedback dates and update URL
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              start_date: this.formatDateForQuery(startDate),
              end_date: this.formatDateForQuery(endDate)
            },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        } else {
          // Default to last 7 days in user's timezone (ending today)
          endDate = moment.tz(this.userTimezone).toDate(); // Today's date
          startDate = moment.tz(this.userTimezone).subtract(6, 'days').startOf('day').toDate();
        }
      } else {
        // Default to last 7 days in user's timezone (ending today)
        endDate = moment.tz(this.userTimezone).toDate(); // Today's date
        startDate = moment.tz(this.userTimezone).subtract(6, 'days').startOf('day').toDate();
      }
    }

    // Create validator for dates not in the future
    const maxDateValidator = (control: AbstractControl) => {
      if (!control.value) return null;
      const selectedDate = new Date(control.value);
      selectedDate.setHours(0, 0, 0, 0); // Start of selected day
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Start of today
      
      return selectedDate > today ? { futureDate: true } : null;
    };

    this.filterForm = new FormGroup({
      startDate: new FormControl(startDate, [Validators.required, maxDateValidator]),
      endDate: new FormControl(endDate, [Validators.required, maxDateValidator]),
    });

    this.feedbackDataSource = new MatTableDataSource<any>([]);

    // Subscribe to query params changes
    this.route.queryParams
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(params => {
        const includeTestsFromUrl = this.parseIncludeTestsQuery(params['include_tests']);
        const includeTestsChanged = includeTestsFromUrl !== this.includeTests;
        if (includeTestsChanged) {
          this.includeTests = includeTestsFromUrl;
        }

        let shouldReload = includeTestsChanged;
        if (params['start_date'] && params['end_date']) {
          const urlStartDate = this.parseDateFromQuery(params['start_date']);
          const urlEndDate = this.parseDateFromQuery(params['end_date']);
          
          // Only update if dates are different to avoid infinite loop
          if (
            urlStartDate && urlEndDate &&
            (
              urlStartDate.getTime() !== this.filterForm.controls.startDate.value?.getTime() ||
              urlEndDate.getTime() !== this.filterForm.controls.endDate.value?.getTime()
            )
          ) {
            this.filterForm.setValue({
              startDate: urlStartDate,
              endDate: urlEndDate
            }, { emitEvent: false });
            shouldReload = true;
          }
        }
        if (shouldReload) {
          this.currentPage = 0;
          this.loadFeedbackList();
        }
      });

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.onFilterFormChange(change);
      });

    // Load initial data
    if (this.integrationId) {
      this.loadFeedbackList();
    } else {
      this.notificationService.openErrorSnackBar('Integration ID not found');
    }
  }

  ngAfterViewInit() {
    this.feedbackDataSource.paginator = this.paginator;
    this.feedbackDataSource.sort = this.sort;
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  formatDate = (date: Date) => {
    return moment(date).format("MMM DD YYYY, h:mm A");
  }

  formatUserName(firstName: string, lastName: string): string {
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else {
      return 'Anonymous';
    }
  }

  formatRating(rating: number): string {
    if (!rating) return 'No rating';
    return `${rating}/5`;
  }

  formatFeedback(feedback: string): string {
    if (!feedback) return 'No feedback';
    return feedback.length > 100 ? feedback.substring(0, 100) + '...' : feedback;
  }

  formatDeviceInfo(deviceInfo: any): string {
    if (!deviceInfo) return 'No device info';
    
    const system = deviceInfo.system_name || 'Unknown OS';
    const version = deviceInfo.system_version || '';
    const make = deviceInfo.make || 'Unknown';
    const model = deviceInfo.model || '';
    
    let deviceString = '';
    
    if (make === 'web') {
      deviceString = `${system} ${version} - ${model}`;
    } else {
      deviceString = `${system} ${version} - ${make} ${model}`;
    }
    
    return deviceString.trim();
  }

  onFilterFormChange(change: any) {
    // Only proceed if form is valid (no future dates)
    if (!this.filterForm.valid) {
      return;
    }
    
    // Reset to first page when filters change
    this.currentPage = 0;
    
    // Update URL with new date parameters
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        start_date: this.formatDateForQuery(change.startDate),
        end_date: this.formatDateForQuery(change.endDate),
        include_tests: this.includeTests
      },
      queryParamsHandling: 'merge'
    });
    
    this.loadFeedbackList();
  }

  onIncludeTestsToggle(checked: boolean): void {
    if (this.includeTests === checked) {
      return;
    }
    this.includeTests = checked;
    this.currentPage = 0;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        include_tests: this.includeTests
      },
      queryParamsHandling: 'merge'
    });
    this.loadFeedbackList();
  }

  loadFeedbackList() {
    if (!this.integrationId) {
      this.notificationService.openErrorSnackBar('Integration ID is required');
      return;
    }

    // Convert selected dates to UTC for API call
    const selectedStartDate = this.filterForm.controls.startDate.value;
    const selectedEndDate = this.filterForm.controls.endDate.value;
    
    // Create dates using simple ISO date strings for the calendar dates
    // This ensures we get the exact calendar days regardless of timezone
    const startDateStr = selectedStartDate.getFullYear() + '-' + 
                        String(selectedStartDate.getMonth() + 1).padStart(2, '0') + '-' + 
                        String(selectedStartDate.getDate()).padStart(2, '0');
    const endDateStr = selectedEndDate.getFullYear() + '-' + 
                      String(selectedEndDate.getMonth() + 1).padStart(2, '0') + '-' + 
                      String(selectedEndDate.getDate()).padStart(2, '0');
    
    const startDate = startDateStr + 'T00:00:00.000Z';
    const endDate = endDateStr + 'T23:59:59.999Z';

    this.uiService
      .waitForEvent(
        this.integrationsService.getThreadFeedbackList(
          this.sessionService.currentAccountValue!.accountID,
          +this.integrationId,
          {
            start_date: startDate,
            end_date: endDate,
            include_tests: this.includeTests,
            page: this.currentPage,
            perPage: this.perPage
          }
        )
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res) => {
          this.feedbackData = res.data || [];
          this.totalItems = res.total || 0;
          this.feedbackDataSource = new MatTableDataSource(this.feedbackData);
          this.feedbackDataSource.paginator = this.paginator;
          this.feedbackDataSource.sort = this.sort;
        },
        () => {
          this.notificationService.openErrorSnackBar(
            'Error retrieving feedback list'
          );
        }
      );
  }

  onPageChange(e: PageEvent) {
    this.localStorageSvc.pageSize = e.pageSize;
    this.perPage = e.pageSize;
    this.currentPage = e.pageIndex;
    this.loadFeedbackList();
  }

  onRowClick(row: any) {
    this.router.navigate(['/assistist', this.route.snapshot.params['featureType'], this.integrationId, 'thread', 'detail', row.id]);
  }

  getTotalFeedback(): number {
    return this.feedbackData?.length || 0;
  }

  getTotalRatings(): number {
    return this.feedbackData?.reduce((total, item) => total + (item.rating ? 1 : 0), 0) || 0;
  }

  getTotalWithFeedback(): number {
    return this.feedbackData?.reduce((total, item) => total + (item.feedback ? 1 : 0), 0) || 0;
  }

  getAverageRating(): number {
    if (!this.feedbackData?.length) return 0;
    
    const ratings = this.feedbackData.filter(item => item.rating).map(item => item.rating);
    return ratings.length > 0 ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length : 0;
  }

  getTotalShares(): number {
    return this.feedbackData?.reduce((total, item) => total + (Number(item.shares) || 0), 0) || 0;
  }

  navigateToUsage() {
    const startDate = this.filterForm.controls.startDate.value;
    const endDate = this.filterForm.controls.endDate.value;
    
    this.router.navigate(['/assistist', this.route.snapshot.params['featureType'], this.integrationId, 'usage-report'], { 
      queryParams: {
        feedback_start_date: this.formatDateForQuery(startDate),
        feedback_end_date: this.formatDateForQuery(endDate),
        include_tests: this.includeTests
      }
    });
  }

  exportToExcel() {
    this.exportFeedback('xls');
  }

  exportToCsv() {
    this.exportFeedback('csv');
  }

  private exportFeedback(format: 'xls' | 'csv') {
    if (!this.integrationId) {
      this.notificationService.openErrorSnackBar('Integration ID is required');
      return;
    }

    const { startDate, endDate } = this.getDateRangeQuery();
    this.uiService
      .waitForEvent(
        this.integrationsService.getThreadFeedbackList(
          this.sessionService.currentAccountValue!.accountID,
          +this.integrationId,
          {
            start_date: startDate,
            end_date: endDate,
            include_tests: this.includeTests,
            page: 0,
            perPage: 999999999
          }
        )
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res) => {
          const rows = res.data || [];
          if (!rows.length) {
            this.notificationService.openErrorSnackBar('No feedback data available to export');
            return;
          }

          const headers = ['Date', 'User', 'Email', 'Rating (Out of 5)', 'Share Count', 'Feedback'];
          const bodyRows = rows.map((row: any) => [
            this.formatDate(row.created_at),
            this.formatUserName(row.first_name, row.last_name),
            row.email || 'N/A',
            Math.trunc(Number(row.rating) || 0),
            Number(row.shares) || 0,
            row.feedback || ''
          ]);

          const filename = moment().format(
            format === 'xls'
              ? '[LHPEFeedback-]YYYY-MM-DD-HH-mm-ss[.xls]'
              : '[LHPEFeedback-]YYYY-MM-DD-HH-mm-ss[.csv]'
          );

          if (format === 'csv') {
            const csvContent = [
              headers.map((value) => this.escapeCsvValue(value)).join(','),
              ...bodyRows.map((row: Array<string | number>) => row.map((value) => this.escapeCsvValue(value)).join(',')),
            ].join('\n');

            this.fileSaver.save(
              new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }),
              filename
            );
            return;
          }

          const htmlRows = bodyRows
            .map((row: Array<string | number>) => `<tr>${row.map((value) => `<td>${this.escapeHtml(value)}</td>`).join('')}</tr>`)
            .join('');
          const html = `<table><thead><tr>${headers.map((h) => `<th>${this.escapeHtml(h)}</th>`).join('')}</tr></thead><tbody>${htmlRows}</tbody></table>`;

          this.fileSaver.save(
            new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' }),
            filename
          );
        },
        () => {
          this.notificationService.openErrorSnackBar('Error exporting feedback');
        }
      );
  }

  private getDateRangeQuery(): { startDate: string; endDate: string } {
    const selectedStartDate = this.filterForm.controls.startDate.value;
    const selectedEndDate = this.filterForm.controls.endDate.value;

    const startDateStr = selectedStartDate.getFullYear() + '-' +
      String(selectedStartDate.getMonth() + 1).padStart(2, '0') + '-' +
      String(selectedStartDate.getDate()).padStart(2, '0');
    const endDateStr = selectedEndDate.getFullYear() + '-' +
      String(selectedEndDate.getMonth() + 1).padStart(2, '0') + '-' +
      String(selectedEndDate.getDate()).padStart(2, '0');

    return {
      startDate: startDateStr + 'T00:00:00.000Z',
      endDate: endDateStr + 'T23:59:59.999Z'
    };
  }

  private parseDateFromQuery(dateString: string): Date | null {
    if (!dateString || typeof dateString !== 'string') {
      return null;
    }

    const parts = dateString.split('-').map(Number);
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }

    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  private formatDateForQuery(date: Date): string {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-');
  }

  private parseIncludeTestsQuery(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      return ['1', 'true', 'yes', 'y', 'on'].includes(value.trim().toLowerCase());
    }
    return false;
  }

  private escapeCsvValue(value: string | number): string {
    const str = String(value ?? '');
    const escaped = str.replace(/"/g, '""');
    return `"${escaped}"`;
  }

  private escapeHtml(value: string | number): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
